import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, CreditCard, Landmark, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { usePreviewGuard } from "@/lib/preview";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const Option = ({ active, onClick, testid, title, children }) => (
  <button type="button" role="radio" aria-checked={active} onClick={onClick} data-testid={testid}
    className={cn("w-full rounded-lg border bg-card p-5 text-left transition-[border-color,box-shadow] duration-200",
      active ? "border-primary ring-2 ring-primary/30" : "hover:border-primary/50")}>
    <div className="flex items-center gap-3">
      <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", active ? "border-primary" : "border-muted-foreground/40")}>
        {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
      </span>
      <span className="font-semibold">{title}</span>
    </div>
    <div className="mt-3 pl-8">{children}</div>
  </button>
);

const PendingList = ({ items }) => (
  <div className="mb-6 rounded-lg border border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.1)] p-4" data-testid="pay-pending-list">
    <div className="flex items-center gap-2 text-sm font-semibold"><Clock className="h-4 w-4" /> Bank payment in progress</div>
    {items.map((t) => (
      <div key={t.id} className="mt-1 text-sm" data-testid={`pay-pending-${t.id}`}>
        <span className="num font-semibold">{money(t.amount_cents)}</span> started {fmtDate(t.started_on)} · {t.message}
      </div>
    ))}
    <div className="mt-1 text-xs text-muted-foreground">The amount due below already excludes it, so you won't pay twice.</div>
  </div>
);

const toCents = (v) => {
  const n = Number(String(v).replace(/[$,\s]/g, ""));
  return v === "" || !Number.isFinite(n) ? null : Math.round(n * 100);
};

const customError = (cents, payoff) => {
  if (cents === null) return "Enter an amount";
  if (cents < 100) return "Minimum payment is $1.00";
  if (cents > payoff) return `Amount can't be more than your payoff of ${money(payoff)}`;
  return "";
};

export default function PayPage() {
  const { id } = useParams();
  const { guard } = usePreviewGuard();
  const [o, setO] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [mode, setMode] = useState("due");
  const [method, setMethod] = useState("ach");
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/portal/loans/${id}/pay`).then((r) => {
      setO(r.data);
      if (r.data.amount_due_cents < 100) setMode("custom");
    }).catch((e) => setLoadErr(apiError(e).message));
  }, [id]);

  const cents = mode === "due" ? o?.amount_due_cents : toCents(amount);
  const submit = guard(async (e) => {
    e?.preventDefault?.();
    const msg = mode === "custom" ? customError(cents, o.payoff_cents) : "";
    setErr(msg);
    if (msg) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/loans/${id}/checkout`, { option: mode, method, amount: mode === "custom" ? amount : null, origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (ex) {
      const { message, fields } = apiError(ex);
      setErr(fields.amount || message);
      toast.error(fields.amount || message);
      setBusy(false);
    }
  });

  const back = <Link to={`/portal/loans/${id}`} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="pay-back"><ArrowLeft className="h-4 w-4" /> Back to loan</Link>;
  if (loadErr) return <div>{back}<div className="py-16 text-center text-muted-foreground" data-testid="pay-not-available">{loadErr}</div></div>;
  if (!o) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;
  const ci = o.current_installment;

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-xl" data-testid="pay-page">
      {back}
      <div className="eyebrow">{o.loan_number} · Online payment</div>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">Make a payment</h1>
      <div className="gold-rule mb-6 mt-4 w-24" />
      {o.pending?.length > 0 && <PendingList items={o.pending} />}
      <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary" data-testid="pay-bank-tip"><Landmark className="h-4 w-4" /> Save time and fees: pay by bank account</p>
      <div className="mb-6 space-y-3" role="radiogroup" aria-label="Payment method">
        <Option active={method === "ach"} onClick={() => setMethod("ach")} testid="pay-method-ach"
          title={<span className="flex flex-wrap items-center gap-2">Pay by bank account — free <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="pay-method-recommended">Recommended</span></span>}>
          <div className="text-xs text-muted-foreground">Connect your bank securely through Stripe. No fee. Usually clears in 3–5 business days.</div>
        </Option>
        <Option active={method === "card"} onClick={() => setMethod("card")} testid="pay-method-card" title="Pay by card">
          <div className="text-xs text-muted-foreground">Debit or credit card. No fee. Applied as soon as it's approved.</div>
        </Option>
      </div>
      <div className="eyebrow mb-2">Amount</div>
      <div className="space-y-3" role="radiogroup">
        <Option active={mode === "due"} onClick={() => { setMode("due"); setErr(""); }} testid="pay-option-due" title="Amount due">
          <div className="num font-display text-3xl font-semibold" data-testid="pay-amount-due">{money(o.amount_due_cents)}</div>
          <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
            {ci && <div>Current installment {ci.number} · {money(ci.amount_cents)} due {fmtDate(ci.due_date)}</div>}
            {o.past_due_cents > 0 && <div>Past due · {money(o.past_due_cents)}</div>}
            {o.fees_cents > 0 && <div>Outstanding fees · {money(o.fees_cents)}</div>}
            {o.pending_cents > 0 && <div data-testid="pay-pending-excluded">Less pending bank payments · −{money(o.pending_cents)}</div>}
          </div>
        </Option>
        <Option active={mode === "custom"} onClick={() => setMode("custom")} testid="pay-option-custom" title="Custom amount">
          <div className="relative max-w-[220px]" onClick={(e) => e.stopPropagation()}>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input inputMode="decimal" placeholder="0.00" value={amount} onFocus={() => setMode("custom")}
              onChange={(e) => { setAmount(e.target.value); setErr(""); }} className={cn("h-11 pl-7 text-lg", err && mode === "custom" && "border-destructive")} data-testid="pay-custom-amount-input" />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Between $1.00 and your payoff amount.</div>
        </Option>
      </div>
      <div className="mt-4 flex items-center justify-between rounded-md bg-muted/50 px-4 py-3 text-sm">
        <span className="text-muted-foreground">Payoff amount (for reference)</span>
        <span className="num font-semibold" data-testid="pay-payoff">{money(o.payoff_cents)}</span>
      </div>
      {err && <p className="mt-3 text-sm font-medium text-destructive" data-testid="pay-error">{err}</p>}
      <Button type="submit" disabled={busy} className="mt-6 h-12 w-full text-base" data-testid="pay-continue-button">
        {busy ? <Loader2 className="animate-spin" /> : method === "ach" ? <Landmark /> : <CreditCard />} Continue to secure checkout{cents >= 100 && !err ? ` · ${money(cents)}` : ""}
      </Button>
      <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground"><Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> You'll finish on Stripe's secure checkout. You pay exactly the amount shown — no fees for bank or card.</p>
    </form>
  );
}
